/**
 * UN IDENTIFICADOR DICTADO, EN CARACTERES (CQ4b · PLAN §3, CQ5).
 *
 * Por voz, un NIF llega como «be cero cuatro uno dos tres cuatro cinco seis» o
 * como «B 04 123 456». El asistente del cuaderno no puede validar eso: primero
 * hay que pasarlo a «B04123456» y DESPUÉS mirar la letra de control con
 * `checkNif`. Lo que sale de aquí NUNCA se guarda sin más: se le repite a la
 * persona («B04123456, ¿es así?») y solo queda firme con un «sí» (A20).
 *
 * PURO y sin imports: va en la copia Deno del motor (scripts/sync-cuaderno.mjs).
 *
 * Qué entiende, palabra a palabra (en español, que es como se dicta un NIF
 * aunque la app esté en otro idioma):
 *   - cifras dichas: «cero», «uno»/«una», «dos»… «nueve», una a una;
 *   - números dichos en grupos (CQ5): «doce», «trescientos cuarenta y cinco»,
 *     «mil doscientos treinta y cuatro», «dos mil diecinueve». Las decenas solo
 *     se juntan con su unidad por «y» («treinta y cuatro» → 34; «treinta
 *     cuatro» son dos grupos, 304): lo que no es español correcto no se adivina;
 *   - letras dichas: «a», «be», «ce», «de», «e», «efe», «ge», «hache», «i»,
 *     «jota», «ka», «ele», «eme», «ene», «eñe», «o», «pe», «cu», «erre», «ese»,
 *     «te», «u», «uve»/«ve», «uve doble»/«doble uve», «equis», «i griega»/«ye»,
 *     «zeta»;
 *   - separadores dichos: «guion» → «-», «barra» → «/», «punto» → «.»
 *     (los ROPO y ROMA los llevan: «04/1234/AS»);
 *   - lo que ya viene escrito («B04», «123», «AS», «04/1234/AS») se deja, en
 *     mayúsculas.
 *
 * Erratas del dictado que se corrigen (CQ5, cómo lo escribe la transcripción y
 * cómo se dicta un NIF en el campo):
 *   - el preámbulo: «El NIF es B04123451.» (Scribe escribe cifras y punto final)
 *     → «B04123451». Antes salía «ELNIFESB04123451»;
 *   - la puntuación pegada: «…cinco uno.» ya no deja «UNO» en vez de «1»;
 *   - el alfabeto de deletrear: «be de Barcelona», «B de Burgos» → «B»; y la
 *     preposición: «el nif DE la empresa» no mete una D;
 *   - «be alta», «be larga», «uve baja», «i latina» → B, B, V, I (la palabra
 *     de detrás se descarta, como toda palabra suelta de cuatro letras o más
 *     que no es nada de lo de arriba: «Barcelona», «empresa»);
 *   - espacios, guiones y puntos entre los trozos («B 04 123 451», «B-04.123.451»).
 * Lo que NO hace: adivinar. Un NIF con la letra de control mala sale tal cual lo
 * dijo la persona y `checkNif` lo rechaza: el asistente lo vuelve a pedir y
 * nunca «corrige» la letra.
 */

const CIFRAS: Record<string, number> = {
  cero: 0,
  uno: 1,
  una: 1,
  un: 1,
  dos: 2,
  tres: 3,
  cuatro: 4,
  cinco: 5,
  seis: 6,
  siete: 7,
  ocho: 8,
  nueve: 9,
};

/** De diez a veintinueve, cada uno en UNA palabra (ya sin tildes: «dieciseis»). */
const DIECES: Record<string, number> = {
  diez: 10,
  once: 11,
  doce: 12,
  trece: 13,
  catorce: 14,
  quince: 15,
  dieciseis: 16,
  diecisiete: 17,
  dieciocho: 18,
  diecinueve: 19,
  veinte: 20,
  veintiuno: 21,
  veintiun: 21,
  veintiuna: 21,
  veintidos: 22,
  veintitres: 23,
  veinticuatro: 24,
  veinticinco: 25,
  veintiseis: 26,
  veintisiete: 27,
  veintiocho: 28,
  veintinueve: 29,
};

const DECENAS: Record<string, number> = {
  treinta: 30,
  cuarenta: 40,
  cincuenta: 50,
  sesenta: 60,
  setenta: 70,
  ochenta: 80,
  noventa: 90,
};

const CENTENAS: Record<string, number> = {
  cien: 100,
  ciento: 100,
  doscientos: 200,
  doscientas: 200,
  trescientos: 300,
  trescientas: 300,
  cuatrocientos: 400,
  cuatrocientas: 400,
  quinientos: 500,
  quinientas: 500,
  seiscientos: 600,
  seiscientas: 600,
  setecientos: 700,
  setecientas: 700,
  ochocientos: 800,
  ochocientas: 800,
  novecientos: 900,
  novecientas: 900,
};

const LETRAS: Record<string, string> = {
  a: "A",
  be: "B",
  ce: "C",
  de: "D",
  e: "E",
  efe: "F",
  ge: "G",
  hache: "H",
  i: "I",
  jota: "J",
  ka: "K",
  ca: "K",
  ele: "L",
  eme: "M",
  ene: "N",
  "eñe": "Ñ",
  o: "O",
  pe: "P",
  cu: "Q",
  erre: "R",
  ere: "R",
  ese: "S",
  te: "T",
  u: "U",
  uve: "V",
  ve: "V",
  equis: "X",
  ekis: "X",
  ye: "Y",
  zeta: "Z",
  ceta: "Z",
  zeda: "Z",
};

const SEPARADORES: Record<string, string> = {
  guion: "-",
  raya: "-",
  barra: "/",
  punto: ".",
};

/** El nombre del dato, que se dice delante («el NIF es…») y no es parte de él. */
const ETIQUETAS = new Set(["nif", "cif", "dni", "nie", "rea", "reafa", "ropo", "roma", "cp", "sigpac", "n"]);

/** Palabras que no aportan nada al identificador («el nif es…», «pues mira…»). */
const RELLENO = new Set([
  ...ETIQUETAS,
  "el", "la", "los", "las", "lo", "es", "son", "era", "seria", "sera", "mi", "su", "sus", "tu", "nuestro", "nuestra",
  "numero", "num", "letra", "codigo", "postal", "y", "con", "que", "del", "al", "por", "para",
  "pues", "vale", "bueno", "ya", "si", "no", "se", "ok", "eso", "esto", "este", "esta", "ahi", "hay", "ver", "mira", "digo", "entonces",
]);

/** Búsqueda en un diccionario SIN heredar de Object («constructor» no es una cifra). */
function de<T>(m: Record<string, T>, k: string | undefined): T | undefined {
  return k !== undefined && Object.prototype.hasOwnProperty.call(m, k) ? m[k] : undefined;
}

/** Minúsculas y sin tildes (salvo la ñ, que distingue «eñe»). */
function plegar(s: string): string {
  // Por trozos entre eñes: la «ñ» se queda y lo demás pierde la tilde.
  return s
    .toLowerCase()
    .split("ñ")
    .map((trozo) => trozo.normalize("NFD").replace(/[̀-ͯ]/g, ""))
    .join("ñ");
}

/** Sin lo que no puede ir en un identificador y sin el punto final pegado («uno.» → «uno»). */
const limpiar = (s: string, letras: RegExp) => s.replace(letras, "").replace(/\.+$/, "");

/** Un número por debajo de mil dicho con palabras: «ciento veintitrés», «doce», «cuarenta y cinco». */
function menorDeMil(p: string[], i: number): { valor: number; fin: number } | null {
  let valor = 0;
  let k = i;
  let hay = false;
  const centena = de(CENTENAS, p[k]);
  if (centena !== undefined) {
    valor += centena;
    k++;
    hay = true;
  }
  const decena = de(DECENAS, p[k]);
  const diez = de(DIECES, p[k]);
  if (decena !== undefined || diez === 20) {
    valor += decena ?? 20;
    k++;
    hay = true;
    // «treinta y cuatro»; también «veinte y cinco», que se oye.
    const unidad = p[k] === "y" ? de(CIFRAS, p[k + 1]) ?? 0 : 0;
    if (unidad > 0) {
      valor += unidad;
      k += 2;
    }
  } else if (diez !== undefined) {
    valor += diez;
    k++;
    hay = true;
  } else if (hay && (de(CIFRAS, p[k]) ?? 0) > 0) {
    // «ciento cinco»
    valor += de(CIFRAS, p[k])!;
    k++;
  }
  return hay ? { valor, fin: k } : null;
}

/**
 * Un número de más de una cifra dicho con palabras, desde la posición i, o null
 * si ahí no empieza uno («cero», «cuatro»… sueltos son cifras, no números).
 */
function leerNumero(p: string[], i: number): { valor: number; fin: number } | null {
  const t = p[i];
  const unidadDeMil = (de(CIFRAS, t) ?? 0) > 0 && p[i + 1] === "mil";
  const empieza =
    de(CENTENAS, t) !== undefined || de(DECENAS, t) !== undefined || de(DIECES, t) !== undefined || t === "mil" || unidadDeMil;
  if (!empieza) return null;
  const delante = unidadDeMil ? { valor: de(CIFRAS, t)!, fin: i + 1 } : menorDeMil(p, i);
  const k = delante ? delante.fin : i;
  if (p[k] !== "mil") return delante;
  let valor = (delante?.valor ?? 1) * 1000;
  let fin = k + 1;
  const resto = menorDeMil(p, fin);
  if (resto) {
    valor += resto.valor;
    fin = resto.fin;
  } else if ((de(CIFRAS, p[fin]) ?? 0) > 0) {
    valor += de(CIFRAS, p[fin])!;
    fin++;
  }
  return { valor, fin };
}

/** Una palabra suelta de cuatro letras o más que no es cifra, número, letra, separador ni relleno. */
function esPalabraSuelta(w: string | undefined): boolean {
  if (!w || !/^[a-zñ]{4,}$/.test(w)) return false;
  if (RELLENO.has(w) || w === "mil" || w === "doble" || w === "griega") return false;
  return [CIFRAS, DIECES, DECENAS, CENTENAS, LETRAS, SEPARADORES].every((m) => de(m as Record<string, unknown>, w) === undefined);
}

/**
 * «be cero cuatro uno dos tres cuatro cinco seis» → «B04123456».
 * «El NIF es B04123451.» → «B04123451».
 * «B-04.123.451» → «B-04.123.451» (los separadores escritos se dejan: el
 * validador de cada campo decide si los quita).
 */
export function normalizarDictado(raw: string | null | undefined): string {
  const texto = String(raw ?? "").trim();
  if (!texto) return "";
  // En un texto normal, lo que va en MAYÚSCULAS es un código («AS», «ES», «AL»).
  const hayMinusculas = /[a-zñáéíóúü]/.test(texto);
  const trozos = texto
    .replace(/[,;:¿?¡!«»"“”()[\]{}]+/g, " ")
    .split(/\s+/)
    .map((c) => ({ cruda: limpiar(c, /[^A-Za-zÑñÁÉÍÓÚÜáéíóúü0-9./-]/g), t: limpiar(plegar(c), /[^a-zñ0-9./-]/g) }))
    .filter((x) => x.t);
  const p = trozos.map((x) => x.t);
  const out: string[] = [];

  for (let i = 0; i < p.length; i++) {
    const t = p[i];
    const cruda = trozos[i].cruda;
    const sig = p[i + 1];

    // Muletillas de dos palabras.
    if ((t === "o" && sig === "sea") || (t === "es" && sig === "decir")) {
      i++;
      continue;
    }

    // Una letra suelta en mayúscula en mitad del texto, o detrás de cifras, es esa
    // letra («12345678 Y» no es «y»).
    if (i > 0 && /^[A-ZÑ]$/.test(cruda) && (hayMinusculas || /\d/.test(p[i - 1]))) {
      out.push(cruda);
      continue;
    }
    // Un código corto en mayúsculas («AS», «ES» del REA). En un texto todo en
    // mayúsculas solo si va delante de cifras y no detrás del nombre del dato
    // («ES 04 079…» sí; «NIF ES 12345678Z», no).
    if (/^[A-ZÑ]{2,4}$/.test(cruda) && !ETIQUETAS.has(t)) {
      const trasEtiqueta = i > 0 && RELLENO.has(p[i - 1]);
      if (hayMinusculas || (!trasEtiqueta && /^\d/.test(sig ?? ""))) {
        out.push(cruda);
        continue;
      }
    }

    // Letras de dos palabras.
    if ((t === "uve" || t === "ve") && sig === "doble") {
      out.push("W");
      i++;
      continue;
    }
    if (t === "doble" && (sig === "uve" || sig === "ve")) {
      out.push("W");
      i++;
      continue;
    }
    if (t === "i" && sig === "griega") {
      out.push("Y");
      i++;
      continue;
    }

    // Números dichos en grupos.
    const n = leerNumero(p, i);
    if (n) {
      out.push(String(n.valor));
      i = n.fin - 1;
      continue;
    }

    const cifra = de(CIFRAS, t);
    if (cifra !== undefined) {
      out.push(String(cifra));
      continue;
    }
    const sep = de(SEPARADORES, t);
    if (sep !== undefined) {
      out.push(sep);
      continue;
    }

    // «de» es la letra D… salvo que sea la preposición («el nif DE la empresa»)
    // o el alfabeto de deletrear («be DE Barcelona»): entonces se salta, y con
    // él la palabra de detrás.
    if (t === "de" && (RELLENO.has(sig ?? "") || esPalabraSuelta(sig))) {
      if (esPalabraSuelta(sig)) i++;
      continue;
    }
    if (RELLENO.has(t)) continue;
    const letra = de(LETRAS, t);
    if (letra !== undefined) {
      out.push(letra);
      continue;
    }

    // Escrito tal cual («b04», «123», «as», «04/1234/as»): en mayúsculas. Una
    // palabra suelta de cuatro letras o más («Barcelona», «alta») se descarta.
    if (/\d/.test(t) || /^[-/]$/.test(t) || /^[a-zñ]{1,3}$/.test(t)) out.push(t.toUpperCase());
  }
  return out.join("");
}
